import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";
import CompanyDetails from "./tabs/CompanyDetails";
import Billing from "./tabs/Billing";
import Bookings from "./tabs/Bookings";
import FleetAndDrivers from "./tabs/FleetAndDrivers";
import Downloads from "./tabs/Downloads";
import Communications from "./tabs/Communications";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import { Steps } from "intro.js-react";
import axiosInstance from "../../utils/axiosInstance";
import { ToastContainer } from 'react-toastify';

const CompanyEdit = ({
	user,
	company,
	statusOptions,
	currencyOptions,
	billingModelOptions,
	csvColumn,
	googleMapsApiKey,
	timeslots,
	carTypeOptions
}) => {
	const [selectedTab, setSelectedTab] = useState("details");
	const [isDialogOpen, setIsDialogOpen] = useState({ show: false, pendingTab: null });
	const [selectedCompany, setSelectedCompany] = useState(company);

	const timeslotSettingsPath = `/companies/${company.id}/timeslot_settings`;
	const notificationSubscriptionsPath = `/companies/${company.id}/notification_subscriptions`;

	const setDefaultValues = (company) => {
		reset({
			// Company Details
			name: company.name,
			address: company.address,
			company_latitude: company.company_latitude,
			company_longitude: company.company_longitude,
			status: company.status,
			brand_primary_color: company.brand_primary_color,
			brand_secondary_color: company.brand_secondary_color,
			logo_url: company.logo_url,

			// Billing
			currency: company.currency,
			billing_model: company.billing_model,
			company_billings: JSON.stringify(company.company_billings),

			// Bookings
			cancellation_range: company.cancellation_range,
			booking_time_range: company.booking_time_range,
			csb_role: company.csb_role,
			opening_time: company.opening_time,
			cut_off_time: company.cut_off_time,
			timezone: company.timezone,
			timeslot_limits: company.timeslot_limits,
			allow_inbound_bookings: company.allow_inbound_bookings ?? true,
			allow_outbound_bookings: company.allow_outbound_bookings ?? true,
			show_cancellation_actor: company.show_cancellation_actor ?? false,

			// Fleet & Drivers
			merchant_fleets: company.merchant_fleets,
			fleets: company.fleets?.map(fleet => ({ label: fleet.name, value: fleet.id })) || [],
			allowed_inbound_car_types: company.allowed_inbound_car_types ?? [],
			allowed_outbound_car_types: company.allowed_outbound_car_types ?? [],

			// Downloads
			csv_column: company.csv_column,
			user_csv_column: company.user_csv_column,

			// Communications
			operations_email: company.operations_email,
			sms_functionality: company.sms_enabled,
			email_functionality: company.emails_enabled,
		})
	}

	const methods = useForm({
		defaultValues: {
			// Company Details
			name: company.name,
			address: company.address,
			company_latitude: company.company_latitude,
			company_longitude: company.company_longitude,
			status: company.status,
			brand_primary_color: company.brand_primary_color,
			brand_secondary_color: company.brand_secondary_color,
			logo_url: company.logo_url,

			// Billing
			currency: company.currency,
			billing_model: company.billing_model,
			company_billings: JSON.stringify(company.company_billings),

			// Bookings
			cancellation_range: company.cancellation_range,
			booking_time_range: company.booking_time_range,
			csb_role: company.csb_role,
			opening_time: company.opening_time,
			cut_off_time: company.cut_off_time,
			timezone: company.timezone,
			timeslot_limits: company.timeslot_limits,
			allow_inbound_bookings: company.allow_inbound_bookings,
			allow_outbound_bookings: company.allow_outbound_bookings,
			show_cancellation_actor: company.show_cancellation_actor ?? false,

			// Fleet & Drivers
			merchant_fleets: company.merchant_fleets,
			fleets: company.fleets?.map(fleet => ({ label: fleet.name, value: fleet.id })) || [],
			allowed_inbound_car_types: company.allowed_inbound_car_types ?? [],
			allowed_outbound_car_types: company.allowed_outbound_car_types ?? [],

			// Downloads
			csv_column: company.csv_column,
			user_csv_column: company.user_csv_column,

			// Communications
			operations_email: company.operations_email,
			sms_functionality: company.sms_enabled,
			email_functionality: company.emails_enabled,
		}
	});

	const { formState: { isDirty }, reset } = methods;

	// Handle URL hash on page load
	useEffect(() => {
		const hash = window.location.hash.replace('#', '');
		if (hash && tabs.find(tab => tab.key === hash)) {
			setSelectedTab(hash);
		}
	}, []);

	const fetchCompany = async () => {
		try {
			const response = await fetch(`/companies/${selectedCompany.id}/edit.json`);
			const data = await response.json();
			setDefaultValues(data.company);
			setSelectedCompany(data.company);
		} catch (error) {
			console.error("Error fetching company:", error);
		}
	};

	const updateCompany = async (data) => {
		try {
			await axiosInstance(`/companies/employees`, {
				method: 'PUT',
				data: { company: data },
				headers: { Accept: 'application/json' },
			});

			await fetchCompany();
		} catch (error) {
			console.error("Failed to update company.")
		}
	};

	const handleChangeTab = (tab) => {
		window.location.hash = tab;
		if (isDirty) {
			setIsDialogOpen({
				show: true,
				pendingTab: tab
			});
			return;
		}
		setSelectedTab(tab);
	};

	const handleDialogClose = (confirm) => {
		if (confirm) {
		  setSelectedTab(isDialogOpen.pendingTab);
			reset();
		}
		setIsDialogOpen({
		  show: false,
		  pendingTab: null
		});
	};
	
	const tabs = [
		{
			title: "Company Details",
			key: "details",
			component: CompanyDetails,
		},
		{
			title: "Billing",
			key: "billing",
			component: Billing,
		},
		{
			title: "Bookings",
			key: "bookings",
			component: Bookings,
		},
		{
			title: "Fleet & Drivers",
			key: "fleet_and_drivers",
			component: FleetAndDrivers,
		},
		{
			title: "Downloads",
			key: "downloads",
			component: Downloads,
		},
		{
			title: "Communications",
			key: "communications",
			component: Communications,
		},
	];

	const renderTabs = () => {
		const TabComponent = tabs.find(tab => tab.key === selectedTab)?.component;
		return (
			<TabComponent
				user={user}
				selectedCompany={selectedCompany}
				selectedTab={selectedTab}
				fetchCompany={fetchCompany}
				updateCompany={updateCompany}
				currencyOptions={currencyOptions}
				billingModelOptions={billingModelOptions}
				statusOptions={statusOptions}
				csvColumn={csvColumn}
				companyAddresses={company.company_addresses}
				selectedCsbRoles={company.selectedCsbRoles}
				selectedFleets={company.selectedFleets}
				googleMapsApiKey={googleMapsApiKey}
				timeslots={timeslots}
				timeslot_limits={company.timeslot_limits}
				carTypeOptions={carTypeOptions}
			/>
		);
	};

	// Company Details steps
	const [introDetails, setIntroDetails] = useState({
		stepsEnabled: false,
		initialStep: 0,
		options: {
		  showProgress: true,
		  showBullets: false,
		  tooltipClass: 'etapathTooltip',
		},
		steps: [
		  {
			element: '.basic_details',
			title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Basic Details",
			intro: "Add details about the company."
		  },
		  {
			element: '.branding',
			title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Branding Details",
			intro: "Add details about the brand."
		  },
		  {
			element: '.save_revert_buttons',
			title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Save",
			intro: "Don't forget to save your changes!"
		  }
		]
	});

	// Billing steps
	const [introBilling, setIntroBilling] = useState({
		stepsEnabled: false,
		initialStep: 0,
		options: {
			showProgress: true,
			showBullets: false,
			tooltipClass: 'etapathTooltip',
		},
		steps: [
			{
				element: '.billing_options',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Billing Options",
				intro: "Select details about billing methods."
			},
			{
				element: '.vehicle_pricing',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Vehicle Pricing",
				intro: "Enter prices per vehicle type."
			},
			{
				element: '.save_revert_buttons',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Save",
				intro: "Don't forget to save your changes!"
			}
		]
	});

	// Bookings steps
	const [introBookings, setIntroBookings] = useState({
		stepsEnabled: false,
		initialStep: 0,
		options: {
			showProgress: true,
			showBullets: false,
			tooltipClass: 'etapathTooltip',
		},
		steps: [
			{
				element: '.limitations',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Limitations",
				intro: "Specify the hours for booking limits."
			},
			{
				element: '.team_manager',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Team Manager",
				intro: "Select roles that can use the team manager along with times for which bookings can be made."
			},
			{
				element: '.timeslot_limitations',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Time Slot Limits",
				intro: "Set time slot limitations for user roles.",
			},
			{
				element: '.company_addresses',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Company Addresses",
				intro: "Edit the company's available addresses."
			},
			{
				element: '.save_revert_buttons',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Save",
				intro: "Don't forget to save your changes!"
			}
		]
	});

	// Fleet and Drivers steps
	const [introFleetAndDrivers, setIntroFleetAndDrivers] = useState({
		stepsEnabled: false,
		initialStep: 0,
		options: {
			showProgress: true,
			showBullets: false,
			tooltipClass: 'etapathTooltip',
		},
		steps: [
			{
				element: '.assigned_fleets',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Assigned Fleets",
				intro: "Select third party fleet permissions and fleets associated with the company."
			},
			{
				element: '.blocked_drivers',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Blocked Drivers",
				intro: "The blocked drivers feature is coming soon!"
			},
			{
				element: '.save_revert_buttons',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Save",
				intro: "Don't forget to save your changes!"
			}
		]
	});

	
	// Downloads steps
	const [introDownloads, setIntroDownloads] = useState({
		stepsEnabled: false,
		initialStep: 0,
		options: {
			showProgress: true,
			showBullets: false,
			tooltipClass: 'etapathTooltip',
		},
		steps: [
			{
				element: '.csv_columns',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> CSV Columns",
				intro: "Choose columns to include in CSV downloads."
			},
			{
				element: '.save_revert_buttons',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Save",
				intro: "Don't forget to save your changes!"
			}
		]
	});
	
	// Communications steps
	const [introCommunications, setIntroCommunications] = useState({
		stepsEnabled: false,
		initialStep: 0,
		options: {
			showProgress: true,
			showBullets: false,
			tooltipClass: 'etapathTooltip',
		},
		steps: [
			{
				element: '.communications',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Communications",
				intro: "Provide details for communication methods."
			},
			{
				element: '.notification_center',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Notification Center",
				intro: "The notification center feature is coming soon!"
			},
			{
				element: '.save_revert_buttons',
				title: "<i class='fas fa-map-marker-alt' style='margin-right: 8px;'></i> Save",
				intro: "Don't forget to save your changes!"
			}
		]
	});

	const managedDetails = (start) => {
		setIntroDetails((prevState) => ({
		  ...prevState,
		  stepsEnabled: start,
		}));
	};

	const managedBilling = (start) => {
		setIntroBilling((prevState) => ({
		  ...prevState,
		  stepsEnabled: start,
		}));
	};

	const managedBookings = (start) => {
		setIntroBookings((prevState) => ({
		  ...prevState,
		  stepsEnabled: start,
		}));
	};

	const managedFleetAndDrivers = (start) => {	
		setIntroFleetAndDrivers((prevState) => ({
		  ...prevState,
		  stepsEnabled: start,
		}));
	};

	const managedDownloads = (start) => {	
		setIntroDownloads((prevState) => ({
		  ...prevState,
		  stepsEnabled: start,
		}));
	};

	const managedCommunications = (start) => {	
		setIntroCommunications((prevState) => ({
		  ...prevState,
		  stepsEnabled: start,
		}));
	};

	const handleButtonClick = () => {
		switch (selectedTab) {
		  case 'details':
			managedDetails(true);
			break;
		  case 'billing':
			managedBilling(true);
			break;
		  case 'bookings':
			managedBookings(true);
			break;
		  case 'fleet_and_drivers':
			managedFleetAndDrivers(true);
			break;
		  case 'downloads':
			managedDownloads(true);
			break;
		  case 'communications':
			managedCommunications(true);
			break;
		  default:
			break;
		}
	};

	return (
		<FormProvider {...methods}>
			<div className="w-full flex flex-col overflow-x-hidden">

				<div className="flex w-full justify-between">
					<div>
						<div className="flex justify-between items-center">
							<h1 className="text-xl font-medium text-gray-700">{company.name} Settings</h1>
							{selectedTab!="details" && selectedTab!="billing" && selectedTab!="bookings" && selectedTab!="fleet_and_drivers" && selectedTab!="downloads" && selectedTab!="communications" == null && (
							<button
								className= 'text-gray-500 cursor-pointer hover:text-gray-900 ml-2'
								onClick={() => managedDetails(true)}
							>
								<i className="fas fa-question-circle"/>
							</button>)}
						</div>
						<p className="text-sm text-gray-500 pb-4">{`Logged in as ${user.name} ${user.surname}`}</p>
					</div>
					<div className="flex items-center gap-4">
						<div className="">
							<a
								href={notificationSubscriptionsPath}
								className="button flex items-center gap-2 text-blue-600 hover:text-blue-800"
							>
								<svg 
									xmlns="http://www.w3.org/2000/svg"
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="lucide lucide-save"
								>
									<path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/>
									<path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/>
									<path d="M7 3v4a1 1 0 0 0 1 1h7"/>
								</svg>

								<span>Manage Notification Subscriptions</span>
							</a>
						</div>

						<div className="">
							<a
								href={timeslotSettingsPath}
								className="button flex items-center gap-2 text-blue-600 hover:text-blue-800"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth="1.5"
									stroke="currentColor"
									className="h-6 w-6"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
									/>
								</svg>
								<span>Manage Timeslot Settings</span>
							</a>
						</div>
					</div>
				</div>

				<div className="company_settings bg-white shadow-md rounded-lg">
					<div className="w-full flex mb-4">
						<div className="flex bg-gray-200 rounded-t-lg w-full">
							{tabs.map((tab, index) =>(
								<button
									key={index}
									className={`${selectedTab === tab.key
									? "border-t border-r border-gray-300 bg-white pt-2 pb-2 px-2 md:px-4 lg:px-8 xl:px-12 text-xs rounded-t-lg flex-grow focus:outline-none"
										: "bg-gray-200 border-b text-gray-700 pt-2 pb-2 px-2 md:px-4 lg:px-6 xl:px-8 text-xs rounded-t-lg flex-grow focus:outline-none"
									}`}
									onClick={() => handleChangeTab(tab.key)}
								>
									{tab.title}
									{selectedTab === tab.key && (
										<span
											className="text-gray-500 cursor-pointer hover:text-gray-900 ml-2"
											onClick={handleButtonClick}
										>
											<i className="fas fa-question-circle" />
										</span>
									)}
								</button>
							))}
						</div>
					</div>
					{renderTabs()}
				</div>

				<Dialog
					open={isDialogOpen.show}
					onClose={() => handleDialogClose(false)}
				>
					<DialogTitle>Unsaved Changes</DialogTitle>
					<DialogContent>
						<DialogContentText>
							You have unsaved changes. Are you sure you want to change to another page?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button 
							onClick={() => handleDialogClose(false)} 
							style={{
								backgroundColor: 'grey',
								color: 'white',
								transition: 'background-color 0.3s ease',
							}}
							onMouseEnter={(e) => (e.target.style.backgroundColor = 'lightgrey')}
							onMouseLeave={(e) => (e.target.style.backgroundColor = 'grey')}
						>
							No
						</Button>
						<Button onClick={() => handleDialogClose(true)} 
							color="primary" 
							autoFocus
							className={`button`}
						>
							Yes
						</Button>
					</DialogActions>
				</Dialog>

				{/* Company Details */}
				<Steps 
					enabled={introDetails.stepsEnabled}
					steps={introDetails.steps}
					initialStep={introDetails.initialStep}
					onExit={() => managedDetails(false)}
					options={introDetails.options}
				/>

				{/* Billing */}
				<Steps 
					enabled={introBilling.stepsEnabled}
					steps={introBilling.steps}
					initialStep={introBilling.initialStep}
					onExit={() => managedBilling(false)}
					options={introBilling.options}
				/>

				{/* Bookings */}
				<Steps 
					enabled={introBookings.stepsEnabled}
					steps={introBookings.steps}
					initialStep={introBookings.initialStep}
					onExit={() => managedBookings(false)}
					options={introBookings.options}
				/>

				{/* Fleet And Drivers */}
				<Steps 
					enabled={introFleetAndDrivers.stepsEnabled}
					steps={introFleetAndDrivers.steps}
					initialStep={introFleetAndDrivers.initialStep}
					onExit={() => managedFleetAndDrivers(false)}
					options={introFleetAndDrivers.options}
				/>

				{/* Downloads */}
				<Steps 
					enabled={introDownloads.stepsEnabled}
					steps={introDownloads.steps}
					initialStep={introDownloads.initialStep}
					onExit={() => managedDownloads(false)}
					options={introDownloads.options}
				/>

				{/* Communications */}
				<Steps 
					enabled={introCommunications.stepsEnabled}
					steps={introCommunications.steps}
					initialStep={introCommunications.initialStep}
					onExit={() => managedCommunications(false)}
					options={introCommunications.options}
				/>
			</div>
			{/* Portal the toasts to the body so the fixed container is never clipped
			    by the page's overflow-hidden layout columns. */}
			{typeof document !== 'undefined' && createPortal(
				<ToastContainer
					position="bottom-right"
					autoClose={5000}
					hideProgressBar={false}
					newestOnTop={false}
					closeOnClick
					rtl={false}
					pauseOnFocusLoss
					draggable
					pauseOnHover
					theme="light"
				/>,
				document.body
			)}
		</FormProvider>
	);
};

CompanyEdit.propTypes = {
	user: PropTypes.object,
	company: PropTypes.shape({
		id: PropTypes.number,
		show_cancellation_actor: PropTypes.bool,
		name: PropTypes.string,
		address: PropTypes.string,
		status: PropTypes.string,
		brand_primary_color: PropTypes.string,
		brand_secondary_color: PropTypes.string,
		logo_url: PropTypes.string,
		currency: PropTypes.string,
		billing_model: PropTypes.string,
		company_billings: PropTypes.array,
		cancellation_range: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		booking_time_range: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		csb_role: PropTypes.string,
		opening_time: PropTypes.string,
		cut_off_time: PropTypes.string,
		timezone: PropTypes.string,
		timeslot_limits: PropTypes.bool,
		allow_inbound_bookings: PropTypes.bool,
		allow_outbound_bookings: PropTypes.bool,
		merchant_fleets: PropTypes.bool,
		fleets: PropTypes.array,
		allowed_inbound_car_types: PropTypes.arrayOf(PropTypes.string),
		allowed_outbound_car_types: PropTypes.arrayOf(PropTypes.string),
		csv_column: PropTypes.object,
		operations_email: PropTypes.string,
		sms_enabled: PropTypes.bool,
		emails_enabled: PropTypes.bool,
		company_latitude: PropTypes.number,
		company_longitude: PropTypes.number,
	}),
	statusOptions: PropTypes.array,
	currencyOptions: PropTypes.array,
	billingModelOptions: PropTypes.array,
	csvColumn: PropTypes.object,
	googleMapsApiKey: PropTypes.string,
	timeslots: PropTypes.array,
	carTypeOptions: PropTypes.arrayOf(PropTypes.shape({
		label: PropTypes.string,
		value: PropTypes.string,
	})),
};

export default CompanyEdit;